<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0 translate-y-4"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-4"
    >
      <div
        v-if="showPrompt"
        class="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 p-4 z-50"
      >
        <!-- Chrome/Edge: Botão de instalação -->
        <div v-if="!isSafari" class="flex items-start gap-3">
          <!-- Ícone -->
          <div class="flex-shrink-0">
            <div
              class="w-12 h-12 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center"
            >
              <img src="/nexxo-icon.png" alt="Nexxo" class="w-8 h-8" />
            </div>
          </div>

          <!-- Conteúdo -->
          <div class="flex-1 min-w-0">
            <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-1">
              Instalar Magneto
            </h3>
            <p class="text-xs text-gray-600 dark:text-gray-400 mb-3">
              Instale o app na sua tela inicial para acesso rápido e offline.
            </p>

            <!-- Botões -->
            <div class="flex gap-2">
              <button
                @click="install"
                class="flex-1 px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium rounded-lg transition-colors"
              >
                Instalar
              </button>
              <button
                @click="dismiss"
                class="px-3 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white text-xs font-medium transition-colors"
              >
                Agora não
              </button>
            </div>
          </div>

          <!-- Botão fechar -->
          <button
            @click="dismiss"
            class="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <v-icon name="io-close" />
          </button>
        </div>

        <!-- Safari/iOS: Instruções manuais -->
        <div v-else class="flex items-start gap-3">
          <!-- Ícone -->
          <div class="flex-shrink-0">
            <div
              class="w-12 h-12 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center"
            >
              <img src="/nexxo-icon.png" alt="Nexxo" class="w-8 h-8" />
            </div>
          </div>

          <!-- Conteúdo -->
          <div class="flex-1 min-w-0">
            <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-2">
              Instalar Magneto {{ isIOS ? 'no iPhone' : 'no Safari' }}
            </h3>

            <!-- Instruções -->
            <div class="space-y-2 mb-3">
              <div class="flex items-start gap-2">
                <span
                  class="flex-shrink-0 w-5 h-5 rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 text-xs font-bold flex items-center justify-center"
                >
                  1
                </span>
                <p class="text-xs text-gray-600 dark:text-gray-400">
                  Toque no botão
                  <span
                    class="inline-flex items-center font-semibold text-blue-600 dark:text-blue-400"
                  >
                    Compartilhar
                    <svg
                      class="w-3 h-3 ml-1"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M16 5l-1.42 1.42-1.59-1.59V16h-1.98V4.83L9.42 6.42 8 5l4-4 4 4zm4 5v11c0 1.1-.9 2-2 2H6c-1.1 0-2-.9-2-2V10c0-1.1.9-2 2-2h3v2H6v11h12V10h-3V8h3c1.1 0 2 .9 2 2z"
                      />
                    </svg>
                  </span>
                  {{ isIOS ? 'na barra inferior' : 'no menu' }}
                </p>
              </div>

              <div class="flex items-start gap-2">
                <span
                  class="flex-shrink-0 w-5 h-5 rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 text-xs font-bold flex items-center justify-center"
                >
                  2
                </span>
                <p class="text-xs text-gray-600 dark:text-gray-400">
                  Selecione
                  <span class="font-semibold">"Adicionar à Tela de Início"</span>
                </p>
              </div>

              <div class="flex items-start gap-2">
                <span
                  class="flex-shrink-0 w-5 h-5 rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 text-xs font-bold flex items-center justify-center"
                >
                  3
                </span>
                <p class="text-xs text-gray-600 dark:text-gray-400">Confirme e pronto! 🎉</p>
              </div>
            </div>

            <!-- Botão -->
            <button
              @click="dismiss"
              class="w-full px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium rounded-lg transition-colors"
            >
              Entendi
            </button>
          </div>

          <!-- Botão fechar -->
          <button
            @click="dismiss"
            class="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <v-icon name="io-close" />
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

const showPrompt = ref(false)
let deferredPrompt: any = null

const DISMISSED_KEY = 'pwa-install-dismissed'
const DISMISSED_EXPIRY = 7 * 24 * 60 * 60 * 1000 // 7 dias

// Detecta Safari e iOS
const isSafari = computed(() => {
  const ua = navigator.userAgent.toLowerCase()
  const isSafariBrowser = ua.includes('safari') && !ua.includes('chrome') && !ua.includes('android')
  return isSafariBrowser
})

const isIOS = computed(() => {
  const ua = navigator.userAgent.toLowerCase()
  return /iphone|ipad|ipod/.test(ua)
})

// Detecta se já está instalado como standalone
const isStandalone = computed(() => {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true
  )
})

onMounted(() => {
  if (isStandalone.value) {
    return
  }

  const dismissed = localStorage.getItem(DISMISSED_KEY)
  if (dismissed) {
    const dismissedTime = parseInt(dismissed)
    if (Date.now() - dismissedTime < DISMISSED_EXPIRY) {
      return
    }
  }

  if (isSafari.value) {
    setTimeout(() => {
      showPrompt.value = true
    }, 5000)
    return
  }

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferredPrompt = e

    setTimeout(() => {
      showPrompt.value = true
    }, 3000)
  })

  window.addEventListener('appinstalled', () => {
    showPrompt.value = false
    deferredPrompt = null
  })
})

const install = async () => {
  if (!deferredPrompt) {
    console.warn('beforeinstallprompt não foi capturado')
    return
  }

  showPrompt.value = false

  deferredPrompt.prompt()

  const { outcome } = await deferredPrompt.userChoice

  if (outcome === 'accepted') {
    console.log('✅ Usuário aceitou instalar')
  } else {
    console.log('❌ Usuário recusou')
  }

  deferredPrompt = null
}

const dismiss = () => {
  showPrompt.value = false
  localStorage.setItem(DISMISSED_KEY, Date.now().toString())
}
</script>
